import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';
import { DiffIcon } from 'icons/common/cadroom';
import { Link } from 'core/routing';
import { useRouterStore } from 'apps/hooks';

import Icon from 'components/Icon';
import { Date } from 'apps/components/date';
import { themes } from 'styles/themes/wikifactoryNew';
import { insertDivider } from 'utils/jsxUtils';
import { renameRevisionToVersion } from 'utils/formatStrings';

import useOutisdeClick from './useOutsideClick';

import {
  DropdownInitialWrapper,
  DropdownContentWrapper,
  RowWrapper,
  RowTitleWrapper,
  RowDescriptionWrapper,
} from './dropdown.styled';

function Dropdown({
  open,
  showDiff,
  iconColor,
  titleStyle,
  contentStyle,
  wrapperStyle,
  selectedRevision,
  revisionsConnection,
}) {
  const routerStore = useRouterStore();
  const { ref, isOpen, setOpen } = useOutisdeClick(false);

  const { route } = routerStore;
  const { v, diff, diffV, ...otherParams } = route.params;
  useEffect(() => {
    setOpen(open);
  }, [open]);

  const options = revisionsConnection.arrayApp.apps.map(
    (obj, index, versions) => {
      const [versionStr] = obj.title.match(/\d+/gi);
      const versionNum = parseInt(versionStr);
      const diffVersion = versions[index + 1];
      const newVersionParams = {
        ...otherParams,
        v: obj.version,
      };

      const key = obj.id + obj.version;

      return (
        <RowWrapper
          key={obj.id}
          selected={selectedRevision?.id === obj.id}
          onClick={() => {
            setOpen(false);
          }}
          style={{
            color: themes.color.wfBlack,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {insertDivider(
            [
              <Link key={key} name={route.name} params={newVersionParams}>
                <RowTitleWrapper style={{ color: themes.color.wfBlack }}>
                  {renameRevisionToVersion(obj.title)}
                </RowTitleWrapper>
              </Link>,
              <div
                key={`${key}description`}
                className='flex w-full items-center justify-between'
              >
                <div className=' min-w-[12.75rem]'>
                  <Link
                    key={obj.id}
                    name={route.name}
                    params={newVersionParams}
                  >
                    <RowDescriptionWrapper
                      style={{ color: themes.color.wfBlack }}
                    >
                      <Trans>
                        <Date value={obj.dateCreated} /> by{' '}
                        {obj.creator.username}
                      </Trans>
                    </RowDescriptionWrapper>
                  </Link>
                </div>
                {showDiff && versionNum > 1 && diffVersion && (
                  <div className='flex w-full justify-end'>
                    <Link
                      className=' w-fit flex items-center cursor-pointer'
                      name={route.name}
                      params={{
                        ...newVersionParams,
                        diff: diffVersion?.file?.id,
                        diffV: diffVersion?.version,
                      }}
                      openTab={{
                        title: obj.file.filename,
                        iconType: 'comparison',
                      }}
                    >
                      <DiffIcon width={16} className='mx-2' />
                      <p className=' whitespace-nowrap min-w-[2.375rem] text-sm text-wfBlack mr-4'>{`${versionNum} vs ${
                        versionNum - 1
                      }`}</p>
                    </Link>
                  </div>
                )}
              </div>,
            ],
            {
              divider: <div className='mx-1' />,
            },
          )}
        </RowWrapper>
      );
    },
  );

  return (
    <div ref={ref}>
      <DropdownInitialWrapper
        style={wrapperStyle}
        onClick={() => setOpen(!isOpen)}
      >
        <RowTitleWrapper style={titleStyle}>
          {renameRevisionToVersion(selectedRevision?.title || '')}
        </RowTitleWrapper>
        <Icon
          colorPath={iconColor}
          name={isOpen ? 'caret_up' : 'caret_down'}
          size='small'
        />
      </DropdownInitialWrapper>
      {isOpen && (
        <DropdownContentWrapper
          style={{
            overflowY: 'scroll',
            boxShadow: '0px 2px 2px gray',
            ...(contentStyle || {}),
          }}
        >
          {options}
        </DropdownContentWrapper>
      )}
    </div>
  );
}

Dropdown.propTypes = {
  revisionsConnection: PropTypes.object,
  wrapperStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  contentStyle: PropTypes.object,
  selectedRevision: PropTypes.object,
  iconColor: PropTypes.string,
  open: PropTypes.bool,
  showDiff: PropTypes.bool,
  showDateAndUser: PropTypes.bool,
};

export default withNamespaces()(Dropdown);
